/* eslint-disable import/named */
/* eslint-disable kf/const-naming-convention */
import Quill from "quill";
import { defineMessage } from "@lingui/macro";

import { cloneDeep } from "base/utils";

import {
  FORMAT_TYPES,
  Bold,
  Italic,
  Strike,
  Header,
  BlockQuote,
  CodeBlock,
  Mark,
  SubScript,
  SuperScript
} from "../index.js";
import {
  LinkWithTooltip as Link,
  MentionComponent
} from "../components/index.js";
import {
  BoldButton,
  ItalicButton,
  SubScriptButton,
  SuperScriptButton,
  LinkButton,
  EraseButton,
  Alignment,
  CodeButton,
  ColorButton,
  Heading,
  BlockQuoteButton,
  StrikeButton,
  UnderlineButton,
  VideoButton,
  Video,
  Header1,
  Header2,
  ULList,
  OLList,
  Image
} from "../buttons/index.js";

import AlignIcon from "../icons/AlignLeft.svg";
import FieldIcon from "../icons/Field.svg";
import ImageIcon from "../icons/Image.svg";
import VideoIcon from "../icons/Video.svg";
import LinkIcon from "../icons/Link.svg";
import NumberedListIcon from "../icons/ListNumber.svg";
import BulletListIcon from "../icons/ListBulletRound.svg";
import ParagraphIcon from "../icons/Paragraph.svg";
import HeadingIcon from "../icons/H1.svg";
import SubHeadingIcon from "../icons/H2.svg";
import SubSubHeadingIcon from "../icons/H3.svg";
import BoldIcon from "../icons/Bold.svg";
import ItalicIcon from "../icons/Italic.svg";
import UnderlineIcon from "../icons/Underline.svg";
import StrikeIcon from "../icons/Strike.svg";
import CodeIcon from "../icons/Code.svg";
import QuoteIcon from "../icons/QuoteFill.svg";

export const TOOLBAR_BUTTONS = {
  BOLD: {
    type: FORMAT_TYPES.BOLD,
    button: BoldButton,
    label: defineMessage`Bold`
  },
  ITALIC: {
    type: FORMAT_TYPES.ITALIC,
    button: ItalicButton,
    label: defineMessage`Italic`
  },
  UNDERLINE: {
    type: FORMAT_TYPES.UNDERLINE,
    button: UnderlineButton,
    label: defineMessage`Underline`
  },
  STRIKE: {
    type: FORMAT_TYPES.STRIKE,
    button: StrikeButton,
    label: defineMessage`Strikethrough`
  },
  COLOR: {
    type: FORMAT_TYPES.COLOR,
    button: ColorButton,
    label: defineMessage`Color`
  },
  ALIGN: {
    type: FORMAT_TYPES.ALIGN,
    button: Alignment,
    label: defineMessage`Alignment`,
    icon: AlignIcon
  },
  HEADER: {
    type: FORMAT_TYPES.HEADER,
    button: Heading
  },
  HEADER1: {
    type: FORMAT_TYPES.H1,
    button: Header1
  },
  HEADER2: {
    type: FORMAT_TYPES.H2,
    button: Header2
  },
  UL_LIST: {
    type: FORMAT_TYPES.UL_LIST,
    button: ULList,
    label: defineMessage`Bulleted list`
  },
  OL_LIST: {
    type: FORMAT_TYPES.OL_LIST,
    button: OLList,
    label: defineMessage`Numbered list`
  },
  CODE: {
    type: FORMAT_TYPES.CODE,
    button: CodeButton,
    label: defineMessage`Code`
  },
  BLOCKQUOTE: {
    type: FORMAT_TYPES.BLOCKQUOTE,
    button: BlockQuoteButton,
    label: defineMessage`Blockquote`
  },
  LINK: {
    type: FORMAT_TYPES.LINK,
    button: LinkButton,
    label: defineMessage`Link`
  },
  ERASE: {
    type: FORMAT_TYPES.ERASE,
    button: EraseButton,
    label: defineMessage`Clear formatting`
  },
  VIDEO: {
    type: FORMAT_TYPES.VIDEO,
    button: VideoButton,
    label: defineMessage`Video`
  },
  SUBSCRIPT: {
    type: FORMAT_TYPES.SUBSCRIPT,
    button: SubScriptButton
  },
  SUPERSCRIPT: {
    type: FORMAT_TYPES.SUPSCRIPT,
    button: SuperScriptButton
  }
};

export const BoldPlugin = {
  formatType: FORMAT_TYPES.BOLD,
  component: Bold
};

export const ItalicPlugin = {
  formatType: FORMAT_TYPES.ITALIC,
  component: Italic
};

export const LinkPlugin = {
  formatType: FORMAT_TYPES.LINK,
  component: Link
};

export const UnderlinePlugin = {
  formatType: FORMAT_TYPES.UNDERLINE,
  component: Quill.import("formats/underline")
};

export const StrikePlugin = {
  formatType: FORMAT_TYPES.STRIKE,
  component: Strike
};

export const CodePlugin = {
  formatType: FORMAT_TYPES.CODE,
  component: CodeBlock
};

export const HeaderPlugin = {
  formatType: FORMAT_TYPES.HEADER,
  component: Header
};

export const ListPlugin = {
  formatType: FORMAT_TYPES.LIST,
  component: Quill.import("formats/list")
};

export const QuotePlugin = {
  formatType: FORMAT_TYPES.BLOCKQUOTE,
  component: BlockQuote
};

export const AlignPlugin = {
  formatType: FORMAT_TYPES.ALIGN,
  component: Quill.import("formats/align")
};

export const ColorPlugin = {
  formatType: FORMAT_TYPES.COLOR,
  component: Quill.import("formats/color")
};

export const BackgroundPlugin = {
  formatType: FORMAT_TYPES.BACKGROUND,
  component: Quill.import("formats/background")
};

export const SubScriptPlugin = {
  formatType: FORMAT_TYPES.SUBSCRIPT,
  component: SubScript
};

export const SuperScriptPlugin = {
  formatType: FORMAT_TYPES.SUPSCRIPT,
  component: SuperScript
};

export const VideoPlugin = {
  formatType: FORMAT_TYPES.VIDEO,
  component: Video
};

export const MentionPlugin = {
  formatType: FORMAT_TYPES.MENTION,
  component: MentionComponent
};

export const ImagePlugin = {
  formatType: FORMAT_TYPES.IMAGE,
  component: Image
};

export const MarkPlugin = {
  formatType: FORMAT_TYPES.MARK,
  component: Mark
};

export const defaultPlugins = [
  BoldPlugin,
  ItalicPlugin,
  LinkPlugin,
  UnderlinePlugin,
  StrikePlugin,
  HeaderPlugin,
  CodePlugin,
  ListPlugin,
  QuotePlugin,
  AlignPlugin,
  ColorPlugin,
  VideoPlugin,
  ImagePlugin,
  SubScriptPlugin,
  SuperScriptPlugin
];

export const plugins = {
  BoldPlugin,
  ItalicPlugin,
  UnderlinePlugin,
  StrikePlugin,
  LinkPlugin,
  HeaderPlugin,
  CodePlugin,
  QuotePlugin,
  ListPlugin,
  AlignPlugin,
  ColorPlugin,
  BackgroundPlugin,
  SubScriptPlugin,
  SuperScriptPlugin,
  VideoPlugin,
  MentionPlugin,
  ImagePlugin,
  MarkPlugin
};

const SLASH_COMMANDS = {
  [FORMAT_TYPES.FIELD]: {
    label: defineMessage`Add field`,
    formatType: FORMAT_TYPES.FIELD,
    icon: FieldIcon
  },
  [FORMAT_TYPES.PROPERTY]: {
    label: defineMessage`Add property`,
    formatType: FORMAT_TYPES.PROPERTY,
    icon: FieldIcon
  },
  [FORMAT_TYPES.IMAGE]: {
    label: defineMessage`Image`,
    formatType: FORMAT_TYPES.IMAGE,
    icon: ImageIcon
  },
  [FORMAT_TYPES.VIDEO]: {
    label: defineMessage`Video clip`,
    formatType: FORMAT_TYPES.VIDEO,
    icon: VideoIcon
  },
  [FORMAT_TYPES.LINK]: {
    label: defineMessage`Link`,
    formatType: FORMAT_TYPES.LINK,
    icon: LinkIcon
  },
  [FORMAT_TYPES.OL_LIST]: {
    label: defineMessage`Numbered list`,
    formatType: FORMAT_TYPES.OL_LIST,
    icon: NumberedListIcon
  },
  [FORMAT_TYPES.UL_LIST]: {
    label: defineMessage`Bulleted list`,
    formatType: FORMAT_TYPES.UL_LIST,
    icon: BulletListIcon
  },
  [FORMAT_TYPES.PARAGRAPH]: {
    label: defineMessage`Paragraph`,
    formatType: FORMAT_TYPES.PARAGRAPH,
    icon: ParagraphIcon
  },
  [FORMAT_TYPES.H1]: {
    label: defineMessage`Heading 1`,
    formatType: FORMAT_TYPES.H1,
    icon: HeadingIcon
  },
  [FORMAT_TYPES.H2]: {
    label: defineMessage`Heading 2`,
    formatType: FORMAT_TYPES.H2,
    icon: SubHeadingIcon
  },
  [FORMAT_TYPES.H3]: {
    label: defineMessage`Heading 3`,
    formatType: FORMAT_TYPES.H3,
    icon: SubSubHeadingIcon
  },
  [FORMAT_TYPES.BOLD]: {
    label: defineMessage`Bold`,
    formatType: FORMAT_TYPES.BOLD,
    icon: BoldIcon
  },
  [FORMAT_TYPES.UNDERLINE]: {
    label: defineMessage`Underline`,
    formatType: FORMAT_TYPES.UNDERLINE,
    icon: UnderlineIcon
  },
  [FORMAT_TYPES.ITALIC]: {
    label: defineMessage`Italic`,
    formatType: FORMAT_TYPES.ITALIC,
    icon: ItalicIcon
  },
  [FORMAT_TYPES.STRIKE]: {
    label: defineMessage`Strikethrough`,
    formatType: FORMAT_TYPES.STRIKE,
    icon: StrikeIcon
  },
  [FORMAT_TYPES.CODE]: {
    label: defineMessage`Code snippet`,
    formatType: FORMAT_TYPES.CODE,
    icon: CodeIcon
  },
  [FORMAT_TYPES.BLOCKQUOTE]: {
    label: defineMessage`Blockquote`,
    formatType: FORMAT_TYPES.BLOCKQUOTE,
    icon: QuoteIcon
  }
};

export const SLASH_COMMANDS_GROUP = [
  {
    label: defineMessage`Field`,
    id: "Field",
    commands: [],
    supportedFormatTypes: [FORMAT_TYPES.FIELD, FORMAT_TYPES.PROPERTY]
  },
  {
    label: defineMessage`Text type`,
    id: "Header",
    commands: [],
    supportedFormatTypes: [
      FORMAT_TYPES.H1,
      FORMAT_TYPES.H2,
      FORMAT_TYPES.H3,
      FORMAT_TYPES.HEADER,
      FORMAT_TYPES.PARAGRAPH
    ]
  },
  {
    label: defineMessage`Attach`,
    id: "Attach",
    commands: [],
    supportedFormatTypes: [
      FORMAT_TYPES.VIDEO,
      FORMAT_TYPES.IMAGE,
      FORMAT_TYPES.LINK
    ]
  },
  {
    label: defineMessage`List`,
    id: "List",
    commands: [],
    supportedFormatTypes: [
      FORMAT_TYPES.OL_LIST,
      FORMAT_TYPES.UL_LIST,
      FORMAT_TYPES.LIST
    ]
  },
  {
    label: defineMessage`Formatting`,
    id: "Formatting",
    commands: [],
    supportedFormatTypes: [
      FORMAT_TYPES.BOLD,
      FORMAT_TYPES.ITALIC,
      FORMAT_TYPES.UNDERLINE,
      FORMAT_TYPES.STRIKE,
      FORMAT_TYPES.CODE,
      FORMAT_TYPES.BLOCKQUOTE
    ]
  }
];

export const constructSlashCommandList = (plugins = []) => {
  const clonedSlashCommandsGroup = cloneDeep(SLASH_COMMANDS_GROUP);
  plugins.forEach(({ formatType, label, icon, renderer, ...restProps }) => {
    const { addAllHeaders = false, hideInSlashCommand = false } = restProps;
    if (!hideInSlashCommand) {
      const isSupported = [
        ...Object.keys(SLASH_COMMANDS),
        FORMAT_TYPES.HEADER,
        FORMAT_TYPES.LIST
      ].includes(formatType);
      if (isSupported) {
        if (formatType === FORMAT_TYPES.LIST) {
          const listGroup = clonedSlashCommandsGroup.find(
            (group) => group.id === "List"
          );
          listGroup.commands.push(SLASH_COMMANDS[FORMAT_TYPES.UL_LIST]);
          listGroup.commands.push(SLASH_COMMANDS[FORMAT_TYPES.OL_LIST]);
        } else if (formatType === FORMAT_TYPES.HEADER) {
          const headerGroup = clonedSlashCommandsGroup.find(
            (group) => group.id === "Header"
          );

          headerGroup.commands.push(SLASH_COMMANDS[FORMAT_TYPES.H1]);
          headerGroup.commands.push(SLASH_COMMANDS[FORMAT_TYPES.H2]);
          addAllHeaders &&
            headerGroup.commands.push(SLASH_COMMANDS[FORMAT_TYPES.H3]);
          headerGroup.commands.push(SLASH_COMMANDS["paragraph"]);
        } else {
          clonedSlashCommandsGroup.find((group) => {
            const { supportedFormatTypes } = group;
            if (supportedFormatTypes.includes(formatType)) {
              const defaultPlugin = SLASH_COMMANDS[formatType];
              if (!label) {
                label = defaultPlugin.label;
              }
              if (!icon) {
                icon = defaultPlugin.icon;
              }
              group.commands.push({
                formatType,
                label,
                icon,
                renderer
              });
              return true;
            }
            return false;
          });
        }
      }
    }
  });
  return clonedSlashCommandsGroup;
};

const UNSUPPORTED_FORMAT_TYPE_GROUP = {
  [FORMAT_TYPES.CODE]: {
    [FORMAT_TYPES.BOLD]: true,
    [FORMAT_TYPES.ITALIC]: true,
    [FORMAT_TYPES.UNDERLINE]: true,
    [FORMAT_TYPES.STRIKE]: true,
    [FORMAT_TYPES.SUBSCRIPT]: true,
    [FORMAT_TYPES.SUPSCRIPT]: true,
    [FORMAT_TYPES.COLOR]: true,
    [FORMAT_TYPES.LINK]: true,
    [FORMAT_TYPES.ALIGN]: true
  },
  [FORMAT_TYPES.BLOCKQUOTE]: {
    [FORMAT_TYPES.IMAGE]: true,
    [FORMAT_TYPES.VIDEO]: true
  }
};

export const isFormatDisabled = (
  disabledFormats = [],
  activeFormats,
  currentFormat
) => {
  return disabledFormats.find(
    (format) =>
      UNSUPPORTED_FORMAT_TYPE_GROUP[format] &&
      UNSUPPORTED_FORMAT_TYPE_GROUP[format][currentFormat] &&
      activeFormats[format]
  );
};
